import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css'

import * as React from 'react'
import { useContext } from 'react'
import LiteYouTubeEmbed from 'react-lite-youtube-embed'

import { RenderReact, type Root } from '@doist/content-system'
import { Api, type Article, type PartialArticle } from '@doist/help-center-utils'
import {
    Box,
    getPlatform,
    Heading,
    helpCenterComponents,
    HelpCenterContextProvider,
} from '@doist/marketist'

import { MoreResources } from 'components/blog-posts/more-resources/more-resources'
import {
    TableOfContents,
    type TableOfContentsProps,
} from 'components/blog-posts/table-of-contents/table-of-contents'
import { ContainerSidebar } from 'components/container-sidebar/container-sidebar'
import { useTranslation } from 'next-i18next'
import { useHelpCenterLabels } from 'services/hooks/use-help-center-labels'

import { Author } from '../author/author'
import { canDisplayMetaInfo, MetaInfo } from '../MetaInfo/MetaInfo'

import css from './ArticleContent.module.css'

import { UserContextV2 } from '@/app/services/providers/contexts/user-context'

type ArticleContentProps = {
    article: Article
    relatedArticles: PartialArticle[]
}

export default function ArticleContent({ article, relatedArticles }: ArticleContentProps) {
    const { t, i18n } = useTranslation('helpcenter')
    const hcLabels = useHelpCenterLabels()
    const { user } = useContext(UserContextV2)

    // Compute the platform selector
    const [currentPlatform, setCurrentPlatform] = React.useState<string>(() => {
        try {
            switch (getPlatform(navigator.userAgent, navigator.platform)) {
                case 'iOS':
                    return 'ios'
                case 'Android':
                    return 'android'
                default:
                    return 'web mac windows-legacy'
            }
        } catch (err) {
            return 'web mac windows-legacy'
        }
    })

    // Compute the article table of contents section
    const tableOfContents: TableOfContentsProps['items'] = []
    if (
        !article.labels.includes('toc-disabled') &&
        article.tableOfContents &&
        article.tableOfContents.headings.length > 0
    ) {
        if (!article.tableOfContents.contentsBeginWithHeading) {
            tableOfContents.push({ id: 'introduction', name: t('article.instructions') })
        }
        tableOfContents.push(
            ...article.tableOfContents.headings.map((heading) => ({
                id: heading.id,
                name: heading.title,
            })),
        )
    }

    // Compute the related articles section
    const [allRelatedArticles, setRelatedArticles] =
        React.useState<PartialArticle[]>(relatedArticles)
    const fetchRelatedArticles = React.useCallback(async () => {
        if (!process.env.NEXT_PUBLIC_ZENDESK_API_URL) {
            return
        }
        const api = new Api(process.env.NEXT_PUBLIC_ZENDESK_API_URL, '', i18n.language)
        const articles = await api.searchArticles(article.title)
        setRelatedArticles(articles.filter((a) => a.id !== article.id).slice(0, 4))
    }, [article, i18n.language])
    React.useEffect(() => {
        if (relatedArticles.length < 4) {
            fetchRelatedArticles()
        }
    }, [fetchRelatedArticles, relatedArticles.length])

    return (
        <ContainerSidebar width="base" className="mbs-24 md-mbe-128 mbe-96">
            <ContainerSidebar.Aside tag="aside" className={css.aside}>
                {canDisplayMetaInfo(article) && <MetaInfo user={user} article={article} />}
                {tableOfContents.length > 0 && (
                    <Box className={css.tableOfContents}>
                        <Heading level="2" size="minor-base" weight="semibold" className="mbe-8">
                            {t('article.tableOfContents')}
                        </Heading>
                        <TableOfContents items={tableOfContents} />
                    </Box>
                )}
                {allRelatedArticles.length > 0 && (
                    <MoreResources
                        title={t('article.related')}
                        items={allRelatedArticles.map((article) => ({
                            title: article.title,
                            url: article.href,
                        }))}
                    />
                )}
                {article.user && !article.labels.includes('author-disabled') ? (
                    <Author user={article.user} updatedAt={article.updatedAt} />
                ) : null}
            </ContainerSidebar.Aside>
            <ContainerSidebar.Main>
                <HelpCenterContextProvider
                    value={{
                        labels: hcLabels,
                        youTubeComponent: LiteYouTubeEmbed,
                        imageMaxWidth: 650,
                    }}
                >
                    <Box id="introduction">
                        <Box tag="article" className={css.content} id={`article-${article.id}`}>
                            <RenderReact
                                content={article.body as unknown as Root}
                                components={helpCenterComponents(
                                    currentPlatform,
                                    setCurrentPlatform,
                                )}
                            />
                        </Box>
                    </Box>
                </HelpCenterContextProvider>
            </ContainerSidebar.Main>
        </ContainerSidebar>
    )
}
